import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMenu, FiX, FiChevronDown, FiPhone, FiMail,
  FiBook, FiUsers, FiActivity, FiAward, FiHome, FiBell,
  FiBarChart2, FiCalendar, FiMessageSquare, FiExternalLink,
} from "react-icons/fi";
import { MdSchool } from "react-icons/md";
import { noticeAPI } from "../../services/api";
import { useSiteSettings } from "../../context/SiteSettingsContext";

const mainNav = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Academics", path: "/academics", children: [
    { label: "Primary School",    path: "/academics#primary",    icon: FiBook,        desc: "Grade 1 – 5" },
    { label: "Secondary School",  path: "/academics#secondary",  icon: FiBook,        desc: "Grade 6 – 10" },
    { label: "High School",       path: "/academics#highschool", icon: FiAward,       desc: "Grade 11 – 12" },
  ]},
  { label: "Admissions", path: "/admissions", children: [
    { label: "Apply Online",       path: "/admissions/apply",        icon: FiExternalLink, desc: "Start your application" },
    { label: "Admission Process",  path: "/admissions#process",      icon: FiActivity,     desc: "Step-by-step guide" },
    { label: "Tuition & Fees",     path: "/admissions#fees",         icon: FiBook,         desc: "Fee structure details" },
    { label: "Scholarships",       path: "/admissions#scholarships", icon: FiAward,        desc: "Financial assistance" },
  ]},
  { label: "Student Life", path: "/student-life", children: [
    { label: "Clubs & Societies",    path: "/student-life#clubs",      icon: FiUsers,    desc: "Join a community" },
    { label: "Sports",               path: "/student-life#sports",     icon: FiActivity, desc: "Athletics & fitness" },
    { label: "Community Activities", path: "/student-life#community",  icon: FiHome,     desc: "Outreach programs" },
    { label: "Facilities",           path: "/student-life#facilities", icon: FiHome,     desc: "Campus resources" },
  ]},
  { label: "News & Events", path: "/news-events" },
  { label: "Gallery", path: "/gallery" },
];

const quickLinks = [
  { label: "Notices",  path: "/notices",           icon: FiBell },
  { label: "Results",  path: "/results",           icon: FiBarChart2 },
  { label: "Calendar", path: "/academics#calendar", icon: FiCalendar },
  { label: "Contact",  path: "/contact",           icon: FiMessageSquare },
];

function Dropdown({ items, isOpen, parentPath, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="absolute top-[calc(100%+8px)] left-0 min-w-[280px] bg-white border border-blue-100 rounded-2xl shadow-xl z-50 overflow-hidden">
          <div className="py-2">
            {items.map((item, i) => (
              <Link key={i} to={item.path} onClick={onClose}
                className="flex items-start gap-4 px-5 py-3.5 hover:bg-[#F0F4FF] transition-colors group border-b border-blue-50 last:border-0">
                <span className="mt-0.5 flex-shrink-0 w-8 h-8 bg-blue-50 group-hover:bg-[#2563EB] rounded-lg flex items-center justify-center transition-colors">
                  <item.icon className="w-4 h-4 text-[#2563EB] group-hover:text-white transition-colors" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-[#0F172A] group-hover:text-[#2563EB] transition-colors">{item.label}</span>
                  {item.desc && <span className="block text-[11px] text-slate-400 mt-0.5">{item.desc}</span>}
                </span>
              </Link>
            ))}
          </div>
          <div className="border-t border-blue-50 px-5 py-3 bg-[#F0F4FF] rounded-b-2xl">
            <Link to={parentPath} onClick={onClose}
              className="flex items-center justify-between text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] transition-colors uppercase tracking-wider">
              View All <FiChevronDown className="w-4 h-4 -rotate-90" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled]           = useState(false);
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [activeIdx, setActiveIdx]         = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const { settings } = useSiteSettings();

  const logoUrl      = settings?.logo?.url || null;
  const schoolName   = settings?.schoolName || "Hillbert English Boarding School";
  const schoolTagline = settings?.schoolTagline || "Inspiring Excellence";
  const phoneNum     = settings?.phone?.primary || "+977-1-4123456";
  const emailAddr    = settings?.email || "info@school.edu.np";

  const [urgentNotices, setUrgentNotices] = useState([]);
  const [noticeIdx, setNoticeIdx]         = useState(0);

  const navigate   = useNavigate();
  const location   = useLocation();
  const wrapRef    = useRef(null);
  const hoverTimer = useRef(null);

  useEffect(() => {
    noticeAPI.getAll({ limit: 100 }).then((res) => {
      const all = res.data?.data?.notices || res.data?.notices || [];
      setUrgentNotices(all);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (urgentNotices.length === 0) return;
    const t = setInterval(() => setNoticeIdx((p) => (p + 1) % urgentNotices.length), 5000);
    return () => clearInterval(t);
  }, [urgentNotices.length]);

  useEffect(() => { setMobileOpen(false); setMobileExpanded(null); }, [location.pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setActiveIdx(null); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const enter = (i) => { clearTimeout(hoverTimer.current); setActiveIdx(i); };
  const leave = () => { hoverTimer.current = setTimeout(() => setActiveIdx(null), 120); };

  return (
    <>
      <header ref={wrapRef} className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md border-b border-blue-100" : "bg-transparent"}`}>

        {/* TOP BAR */}
        <div className={`bg-[#172554] transition-all duration-300 overflow-hidden ${scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"}`}>
          <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 h-10 flex items-center justify-between">
            <div className="hidden sm:flex items-center gap-6 text-blue-200/70 text-[10px] font-semibold uppercase tracking-widest">
              <span className="flex items-center gap-2"><FiPhone className="w-3 h-3 text-sky-400" />{phoneNum}</span>
              <span className="flex items-center gap-2"><FiMail className="w-3 h-3 text-sky-400" />{emailAddr}</span>
            </div>
            <div className="flex items-center gap-5 ml-auto">
              {quickLinks.map((ql, i) => (
                <NavLink key={i} to={ql.path}
                  className={({ isActive }) => `flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${isActive ? "text-sky-300" : "text-blue-300/60 hover:text-white"}`}>
                  <ql.icon className="w-3 h-3" />
                  <span className="hidden md:inline">{ql.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        {/* MAIN NAV */}
        <div className={`transition-all duration-300 ${scrolled ? "py-3 bg-white" : "bg-[#1e3a8a] py-4"}`}>
          <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
            <div className="flex items-center justify-between gap-8">

              {/* Logo */}
              <Link to="/" className="flex items-center gap-4 shrink-0">
                {logoUrl ? (
                  <div className={`flex items-center justify-center bg-white rounded-xl p-2 border border-blue-100 shadow-sm transition-all ${scrolled ? "h-11 w-11" : "h-14 w-14"}`}>
                    <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" />
                  </div>
                ) : (
                  <div className={`flex items-center justify-center bg-[#2563EB] rounded-xl shadow-sm transition-all ${scrolled ? "h-10 w-10" : "h-12 w-12"}`}>
                    <MdSchool className={`text-white ${scrolled ? "w-5 h-5" : "w-6 h-6"}`} />
                  </div>
                )}
                <div className="min-w-0">
                  <p className={`font-bold uppercase tracking-wide leading-none ${scrolled ? "text-lg text-[#0F172A]" : "text-xl text-white"}`}>
                    {schoolName.split(' ')[0]}{" "}
                    <span className={scrolled ? "text-[#2563EB]" : "text-sky-300"}>{schoolName.split(' ')[1]}</span>
                  </p>
                  <p className={`text-[9px] font-semibold uppercase tracking-[0.2em] mt-1 ${scrolled ? "text-slate-400" : "text-blue-200/60"}`}>
                    {schoolTagline}
                  </p>
                </div>
              </Link>

              {/* Desktop Links */}
              <nav className="hidden lg:flex items-center gap-4 xl:gap-5 flex-1 justify-center flex-nowrap">
                {mainNav.map((link, i) => (
                  <div key={i} className="relative shrink-0" onMouseEnter={() => link.children && enter(i)} onMouseLeave={() => link.children && leave()}>
                    {link.children ? (
                      <>
                        <button onClick={() => navigate(link.path)}
                          className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap transition-colors ${activeIdx === i ? "text-sky-300" : scrolled ? "text-slate-600 hover:text-[#2563EB]" : "text-blue-100 hover:text-white"}`}>
                          {link.label}
                          <FiChevronDown className={`w-3 h-3 transition-transform duration-200 ${activeIdx === i ? "rotate-180" : ""}`} />
                        </button>
                        <Dropdown items={link.children} isOpen={activeIdx === i} parentPath={link.path} onClose={() => setActiveIdx(null)} />
                      </>
                    ) : (
                      <NavLink to={link.path} end={link.path === "/"}
                        className={({ isActive }) => `text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-sky-300 border-b-2 border-sky-300 pb-0.5" : scrolled ? "text-slate-600 hover:text-[#2563EB]" : "text-blue-100 hover:text-white"}`}>
                        {link.label}
                      </NavLink>
                    )}
                  </div>
                ))}
              </nav>

              {/* CTA Button */}
              <div className="hidden lg:flex items-center shrink-0">
                <Link to="/admissions/apply"
                  className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 ${scrolled ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]" : "bg-white text-[#1D4ED8] hover:bg-blue-50"}`}>
                  Apply Now
                </Link>
              </div>

              {/* Mobile Hamburger */}
              <button onClick={() => setMobileOpen(!mobileOpen)}
                className={`lg:hidden p-2.5 rounded-xl transition-colors ${scrolled ? "bg-blue-50 text-[#2563EB] hover:bg-blue-100" : "bg-white/10 text-white hover:bg-white/20"}`}>
                <AnimatePresence mode="wait" initial={false}>
                  {mobileOpen
                    ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}><FiX className="w-5 h-5" /></motion.span>
                    : <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}><FiMenu className="w-5 h-5" /></motion.span>}
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>

        {/* MOBILE MENU */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.2 }}
              className="lg:hidden bg-white border-t border-blue-100 shadow-xl relative z-40 max-h-[85vh] overflow-y-auto rounded-b-2xl">
              <div className="p-5 space-y-1">
                {mainNav.map((link, i) => (
                  <div key={i} className="border-b border-blue-50 pb-1">
                    {link.children ? (
                      <>
                        <button onClick={() => setMobileExpanded(mobileExpanded === i ? null : i)}
                          className="w-full flex items-center justify-between py-3.5 text-sm font-semibold text-[#0F172A] uppercase tracking-wide hover:text-[#2563EB] transition-colors">
                          {link.label}
                          <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileExpanded === i ? "rotate-180 text-[#2563EB]" : "text-slate-400"}`} />
                        </button>
                        <AnimatePresence>
                          {mobileExpanded === i && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                              <div className="pl-4 border-l-2 border-[#2563EB]/20 ml-2 mb-4 space-y-3 pt-2">
                                {link.children.map((child, j) => (
                                  <Link key={j} to={child.path} onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-3 text-slate-500 hover:text-[#2563EB] transition-colors">
                                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                                      <child.icon className="w-4 h-4 text-[#2563EB]" />
                                    </div>
                                    <div>
                                      <span className="block text-xs font-semibold uppercase tracking-wide text-[#0F172A]">{child.label}</span>
                                      {child.desc && <span className="block text-[10px] mt-0.5 text-slate-400">{child.desc}</span>}
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <NavLink to={link.path} end={link.path === "/"} onClick={() => setMobileOpen(false)}
                        className={({ isActive }) => `block py-3.5 text-sm font-semibold uppercase tracking-wide transition-colors ${isActive ? "text-[#2563EB]" : "text-[#0F172A] hover:text-[#2563EB]"}`}>
                        {link.label}
                      </NavLink>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-5 bg-[#F0F4FF] border-t border-blue-100 rounded-b-2xl">
                <Link to="/admissions/apply" onClick={() => setMobileOpen(false)}
                  className="block w-full py-3.5 bg-[#2563EB] text-white text-center text-sm font-bold uppercase tracking-wider rounded-xl hover:bg-[#1D4ED8] transition-colors shadow-md">
                  Apply Now
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* NOTICES BANNER */}
      <AnimatePresence>
        {urgentNotices.length > 0 && (
          <motion.div key="notice-banner"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 z-[49] bg-[#2563EB] overflow-hidden" style={{ marginTop: "var(--navbar-height, 0px)" }}>
            <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
              <div className="flex items-center gap-4 py-2.5 text-white font-semibold text-xs uppercase tracking-widest">
                <FiBell className="w-4 h-4 flex-shrink-0 animate-bounce" />
                <div className="flex-1 min-w-0">
                  <Link to="/notices" className="hover:underline line-clamp-1">{urgentNotices[noticeIdx]?.title}</Link>
                </div>
                <button onClick={() => setNoticeIdx((p) => (p + 1) % urgentNotices.length)}
                  className="text-white/70 hover:text-white transition-colors flex-shrink-0">
                  <FiChevronDown className="w-4 h-4 rotate-180" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
